import Container from "@vfuk/core-container";
import Paragraph from "@vfuk/core-paragraph/";
import Heading from "@vfuk/core-heading";
import { useTranslation } from "react-i18next";
import { Stack, IconLink } from "@ucchub/ui";
import DetailsRow from "./DetailsRow";
import { CustomerDetails, OrganisationStatus } from "./Customer.types";
import useCustomerPermissions from "@hooks/usePermission";

type Props = {
  customer: CustomerDetails;
};

export const STATUS_TRANSLATE_KEYS = {
  [OrganisationStatus.ACTIVE]: "active",
  [OrganisationStatus.INACTIVE]: "inactive",
  [OrganisationStatus.DRAFT]: "draft",
};

const Customer = ({ customer }: Props) => {
  const { t } = useTranslation();

  const editLink = `/customers/${customer.id}/edit-new`;

  const notAvailableTxt = t("core:common:not_available");

  const statusTranslateKey = STATUS_TRANSLATE_KEYS[customer.status];
  const { permissions, isPermissionLoading } = useCustomerPermissions();
  return (
    <Container appearance="primary" elevation>
      <Stack justifyContent="space-between" align="baseline">
        <Heading level={4} weight={3}>
          {t("core:customers:customer_details")}
        </Heading>
        {!isPermissionLoading && permissions?.manageCustomers && (
          <IconLink
            icon={{ justify: "left", name: "edit" }}
            text={t("core:customers:edit")}
            appearance="secondary"
            customRouterProps={{
              to: editLink,
            }}
          />
        )}
      </Stack>
      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:name")}
          </Paragraph>
          <Paragraph size={2}>{customer.name}</Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:status")}
          </Paragraph>
          <Paragraph size={2}>
            {statusTranslateKey && t(`core:customers:${statusTranslateKey}`)}
          </Paragraph>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:address_line1")}
          </Paragraph>
          <Paragraph size={2}>
            {customer.addressLine1 || notAvailableTxt}
          </Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:address_line2")}
          </Paragraph>
          <Paragraph size={2}>
            {customer.addressLine2 || notAvailableTxt}
          </Paragraph>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:country")}
          </Paragraph>
          <Paragraph size={2}>{customer.country || notAvailableTxt}</Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:city")}
          </Paragraph>
          <Paragraph size={2}>{customer.city || notAvailableTxt}</Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:area")}
          </Paragraph>
          <Paragraph size={2}>{customer.area || notAvailableTxt}</Paragraph>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:post_code")}
          </Paragraph>
          <Paragraph size={2}>{customer.postcode || notAvailableTxt}</Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:phone")}
          </Paragraph>
          <Paragraph size={2}>
            {customer.phoneNumber || notAvailableTxt}
          </Paragraph>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:contact_first_name")}
          </Paragraph>
          <Paragraph size={2}>
            {customer.contactFirstName || notAvailableTxt}
          </Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:contact_last_name")}
          </Paragraph>
          <Paragraph size={2}>
            {customer.contactLastName || notAvailableTxt}
          </Paragraph>
        </div>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:email")}
          </Paragraph>
          <Paragraph size={2}>
            {customer.contactEmail || notAvailableTxt}
          </Paragraph>
        </div>
      </DetailsRow>
      <DetailsRow>
        <div>
          <Paragraph size={2} weight={3} noMargin>
            {t("core:customers:label:pid")}
          </Paragraph>
          <Paragraph size={2}>{customer.pid || notAvailableTxt}</Paragraph>
        </div>
      </DetailsRow>
    </Container>
  );
};

export default Customer;
